import React, { useState, useEffect } from "react";
import {
  Sparkles,
  Bot,
  Globe,
  FileSearch,
  Languages,
  Briefcase,
  Users,
  Check,
  ArrowRight,
  Plus,
  Clock,
  Building,
  Star,
  Zap,
  Shield,
  Minus,
  ChevronDown,
  Cpu,
  Network,
} from "lucide-react";
import ContactModal from "./modal/ContactModal";
import { useNavigate } from "react-router-dom";
import MainPreview from "./pages/MainPreview";
export default function Main() {
  const navigate = useNavigate();
  const [selectedServices, setSelectedServices] = useState([]);
  const [hoveredPlan, setHoveredPlan] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const services = [
    {
      id: "assistant",
      icon: Bot,
      name: "24시간 AI비서",
      desc: "언제나 당신 곁에",
      color: "from-blue-500 to-indigo-600",
    },
    {
      id: "translate",
      icon: Languages,
      name: "고급 번역 AI",
      desc: "전문가급 번역",
      color: "from-purple-500 to-pink-600",
    },
    {
      id: "research",
      icon: FileSearch,
      name: "딥 리서치 AI",
      desc: "문서 심층 분석",
      color: "from-emerald-500 to-teal-600",
    },
    {
      id: "insight",
      icon: Globe,
      name: "인사이트 분석 AI",
      desc: "웹 데이터 통찰",
      color: "from-orange-500 to-red-600",
    },
    {
      id: "agents",
      icon: Users,
      name: "AI 에이전트",
      desc: "맞춤형 자동화",
      color: "from-cyan-500 to-blue-600",
    },
    {
      id: "company",
      icon: Building,
      name: "가상 AI 회사",
      desc: "아이디어 실현",
      color: "from-rose-500 to-pink-600",
    },
  ];

  const plans = {
    ZERO: {
      price: "10,900",
      desc: "기본 기능",
      features: ["24시간 AI 비서", "AI 에이전트"],
      gradient: "from-gray-600 to-gray-800",
    },
    Basic: {
      price: "~19,900",
      desc: "개인 & 소규모 팀",
      features: [
        "월 결제액 19,900원 이하",
        "ZERO의 모든 기능",
        "선택한 서비스 이용",
        "기본 응답 속도",
      ],
      gradient: "from-slate-600 to-slate-800",
    },
    Pro: {
      price: "~99,900",
      desc: "팀 & 기업",
      popular: true,
      features: [
        "월 결제액 99,900원 이하",
        "Basic의 모든 기능",
        "우선 처리 속도",
        "팀 협업 가능",
        "전담 매니저",
        "AI 비서 무제한",
      ],
      gradient: "from-blue-600 to-purple-600",
    },
    MAX: {
      price: "100,000+",
      desc: "기업",
      features: [
        "월 100,000원 이상",
        "Pro의 모든 기능",
        "AI 에이전트 무제한",
        "신규 기능 얼리 액세스",
      ],
      gradient: "from-purple-600 to-pink-600",
    },
  };

  const toggleService = (serviceId) => {
    setSelectedServices((prev) =>
      prev.includes(serviceId)
        ? prev.filter((id) => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const getRecommendedPlan = () => {
    const count = selectedServices.length;
    if (count <= 2) return "Basic";
    if (count <= 4) return "Pro";
    return "MAX";
  };

  return (
    <>
      <div className="min-h-screen bg-black text-white overflow-x-hidden">
        {/* Animated Background */}
        <div className="fixed inset-0 opacity-30">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600 rounded-full filter blur-[128px] animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-600 rounded-full filter blur-[128px] animate-pulse delay-700"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-pink-600 rounded-full filter blur-[200px] animate-pulse delay-1000"></div>
        </div>

        {/* Hero Section */}
        <div className="relative z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-24">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-lg border border-white/20 px-4 py-2 rounded-full text-sm font-medium mb-6 animate-fade-in">
                <Sparkles className="w-4 h-4 text-yellow-400" />
                <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  엔터프라이즈급 AI 솔루션
                </span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-6 animate-fade-in-up">
                <span className="block">AI Elva와 함께</span>
                <span className="block mt-2 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                  바쁜 업무를 단숨에 가볍게
                </span>
              </h1>

              <p className="text-lg sm:text-xl text-gray-400 mb-10 max-w-3xl mx-auto animate-fade-in-up delay-100">
                필요한 AI 서비스만 선택하여 조합하는 맞춤형 솔루션으로 <br />
                비용 효율적인 업무 자동화를 경험하세요
              </p>

              <button
                className="group relative bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-4 rounded-2xl font-semibold text-lg overflow-hidden transition-all duration-300 hover:scale-105 animate-fade-in-up delay-200"
                onClick={() => navigate("/app")}
              >
                <span className="relative z-10 flex items-center gap-2">
                  대시보드로 이동하기
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
            </div>
          </div>
        </div>

        {/* Service Selection Visual */}
        <div className="relative z-10 py-12 lg:py-24 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
                차세대 플러그인 대시보드
              </h2>
              <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto">
                플러그인형 AI 서비스 중 원하는 기능을 모듈 단위로 구성해보세요.
                <br />
                불필요한 비용은 최소화하고 효율은 극대화할 수 있습니다.
              </p>
            </div>

            {/* Visual Dashboard Preview */}
            <div className="relative">
              {/* Main Dashboard Container */}
              <div className="relative bg-gray-900/50 backdrop-blur-xl border border-gray-800 rounded-3xl p-8 overflow-hidden">
                {/* Animated Background Grid */}
                <div className="absolute inset-0 opacity-10">
                  <div
                    className="absolute inset-0"
                    style={{
                      backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 1px)`,
                      backgroundSize: "40px 40px",
                    }}
                  ></div>
                </div>

                {/* Service Selection Flow */}
                <div className="relative grid lg:grid-cols-2 gap-12 items-center">
                  {/* Left: Service Icons */}
                  <div className="space-y-6">
                    <h3 className="text-2xl font-semibold mb-8">
                      <span className="text-gray-400">
                        다양한 AI 서비스에서
                      </span>
                      <br />
                      <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                        필요한 것만 선택
                      </span>
                    </h3>

                    {/* Animated Service Grid */}
                    <div className="grid grid-cols-3 gap-4">
                      {services.map((service, index) => (
                        <div
                          key={service.id}
                          className="relative group animate-float"
                          style={{
                            animationDelay: `${index * 200}ms`,
                            animationDuration: "3s",
                          }}
                        >
                          <div
                            className={`bg-gradient-to-br ${service.color} p-4 rounded-2xl opacity-80 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-110`}
                          >
                            <h4 className="text-sm font-semibold text-white mb-1">
                              {service.name}
                            </h4>
                            <p className="text-xs text-white/80">
                              {service.desc}
                            </p>
                          </div>
                          <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        </div>
                      ))}
                    </div>

                    {/* Plus More Services */}
                    <div className="flex items-center gap-3 text-gray-400">
                      <Plus className="w-5 h-5" />
                      <span className="text-sm">
                        그 외 다양한 AI 서비스들...
                      </span>
                    </div>
                  </div>

                  {/* Center: Arrow Flow */}
                  <div className="hidden lg:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-0.5 bg-gradient-to-r from-transparent via-purple-500 to-transparent animate-pulse"></div>
                      <ChevronDown className="w-8 h-8 text-purple-500 rotate-[-90deg] animate-pulse" />
                      <div className="w-20 h-0.5 bg-gradient-to-r from-transparent via-purple-500 to-transparent animate-pulse"></div>
                    </div>
                  </div>

                  {/* Right: Custom Dashboard */}
                  <div className="relative">
                    <div className="bg-black/50 backdrop-blur-xl border border-gray-700 rounded-2xl p-6 transform perspective-1000 rotate-y-[-5deg]">
                      <div className="flex items-center justify-between mb-6">
                        <h4 className="text-lg font-semibold">
                          나만의 대시보드
                        </h4>
                        <div className="flex gap-1">
                          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                          <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        </div>
                      </div>

                      {/* Mini Dashboard Layout */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-gradient-to-br from-blue-500/20 to-transparent border border-blue-500/30 rounded-lg p-3">
                          <Bot className="w-5 h-5 text-blue-400 mb-1" />
                          <p className="text-xs text-gray-300">AI 비서</p>
                        </div>
                        <div className="bg-gradient-to-br from-purple-500/20 to-transparent border border-purple-500/30 rounded-lg p-3">
                          <Languages className="w-5 h-5 text-purple-400 mb-1" />
                          <p className="text-xs text-gray-300">고급 번역 AI</p>
                        </div>
                        <div className="bg-gradient-to-br from-emerald-500/20 to-transparent border border-emerald-500/30 rounded-lg p-3">
                          <FileSearch className="w-5 h-5 text-emerald-400 mb-1" />
                          <p className="text-xs text-gray-300">딥 리서치 AI</p>
                        </div>
                        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 flex items-center justify-center">
                          <Plus className="w-5 h-5 text-gray-500" />
                        </div>
                      </div>

                      {/* Cost Indicator */}
                      <div className="mt-4 pt-4 border-t border-gray-700">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-400">월 비용</span>
                          <span className="font-semibold text-green-400">
                            ₩29,900
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Floating Badge */}
                    <div className="absolute -top-4 -right-4 bg-gradient-to-r from-green-500 to-emerald-500 px-4 py-2 rounded-full text-sm font-semibold animate-bounce">
                      비용 절감!
                    </div>
                  </div>
                </div>

                {/* Bottom Banner */}
                <div className="mt-12 bg-gradient-to-r from-blue-900/30 to-purple-900/30 backdrop-blur-xl rounded-2xl p-6 text-center">
                  <p className="text-lg font-medium mb-2">
                    <span className="text-gray-300">선택한 서비스로 </span>
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                      맞춤형 대시보드가 자동 구성됩니다
                    </span>
                  </p>
                  <p className="text-sm text-gray-400">
                    월별로 서비스를 추가하거나 제거할 수 있어 유연한 비용 관리가
                    가능합니다
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Why Choose Elva Section - 새로 추가됨! */}
        <div className="relative z-10 py-12 lg:py-24 bg-gradient-to-b from-black to-gray-900/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
                Elva를 선택해야 하는 이유
              </h2>
              <p className="text-lg sm:text-xl text-gray-400">
                기업의 성장을 위한 최고의 AI 파트너
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Reason 1: Security */}
              <div className="group relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative bg-gray-900/50 backdrop-blur-xl border border-gray-800 rounded-2xl p-8 h-full group-hover:border-blue-700/50 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <Shield className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-3">
                        엔터프라이즈급 보안
                      </h3>
                      <p className="text-gray-400 leading-relaxed">
                        Elva는 기업의 데이터를 AI 학습데이터로 사용하지 않으며,
                        모든 내부 데이터는 특정 서비스를 제외하고 저장하지
                        않습니다. 저장된 문서는 고도의 암호화 알고리즘으로
                        보호됩니다.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Reason 2: Cost Efficiency */}
              <div className="group relative">
                <div className="absolute inset-0 bg-gradient-to-br from-green-600/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative bg-gray-900/50 backdrop-blur-xl border border-gray-800 rounded-2xl p-8 h-full group-hover:border-green-700/50 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-700 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <Zap className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-3">비용 절감</h3>
                      <p className="text-gray-400 leading-relaxed">
                        필요없는 서비스는 구성에서 제외하고 필요한 서비스만
                        사용하여 지불하는 합리적인 가격 정책으로 예산을
                        최적화하세요.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Reason 3: Service Integration */}
              <div className="group relative">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative bg-gray-900/50 backdrop-blur-xl border border-gray-800 rounded-2xl p-8 h-full group-hover:border-purple-700/50 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <Users className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-3">
                        통합 솔루션
                      </h3>
                      <p className="text-gray-400 leading-relaxed">
                        사무 업무부터 마케팅 기획, 번역, 데이터 분석까지 —
                        하나의 플랫폼에서 손쉽게 처리하세요.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Reason 4: Custom Solutions */}
              <div className="group relative">
                <div className="absolute inset-0 bg-gradient-to-br from-pink-600/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative bg-gray-900/50 backdrop-blur-xl border border-gray-800 rounded-2xl p-8 h-full group-hover:border-pink-700/50 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-rose-700 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <Briefcase className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-3">
                        기업별 맞춤형 AI 솔루션 제작
                      </h3>
                      <p className="text-gray-400 leading-relaxed">
                        상시 대기 중인 Elva 팀과 지속적으로 소통하며 귀사에
                        최적화된 AI 도구를 함께 설계할 수 있습니다. 필요에 따라
                        협의하여 원하는 기능을 반영한 솔루션을 구축해보세요.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <style jsx>{`
          @keyframes float {
            0%,
            100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-10px);
            }
          }
          .animate-float {
            animation: float 3s ease-in-out infinite;
          }
        `}</style>

        {/* A2A Protocol Innovation Section */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-5 blur-3xl"></div>
            <div className="relative bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl p-1">
              <div className="bg-gray-900 rounded-3xl p-8 md:p-12">
                <div className="flex items-center justify-center mb-6">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 blur-lg opacity-50"></div>
                    <div className="relative bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-2xl px-6 py-3 font-bold text-lg flex items-center gap-2">
                      <Zap className="w-5 h-5" />
                      Elva만의 독보적인 기술력
                    </div>
                  </div>
                </div>

                <h3 className="text-4xl font-bold text-center mb-6 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  A2A Protocol Elva.Ver
                </h3>

                <p className="text-center text-white mb-8 text-lg">
                  <span className="font-semibold text-white">Elva팀</span>
                  에서 Google의 A2A 프로토콜을 재해석하여
                  <br />
                  회의에 참여하는 모든 AI가 독립된 인격을 가지고 소통하는
                  <br />
                  <span className="font-bold text-purple-600">
                    차세대 시뮬레이션 엔진
                  </span>
                </p>

                <div className="grid md:grid-cols-3 gap-6 mt-10">
                  <div className="text-center">
                    <div className="flex justify-center mb-3">
                      <Network className="w-10 h-10 text-purple-600" />
                    </div>
                    <h4 className="font-semibold text-white mb-1">
                      독립된 AI 인격
                    </h4>
                    <p className="text-sm text-gray-300">
                      각 AI가 고유한 관점과 전문성을 보유
                    </p>
                  </div>

                  <div className="text-center">
                    <div className="flex justify-center mb-3">
                      <Cpu className="w-10 h-10 text-pink-600" />
                    </div>
                    <h4 className="font-semibold text-white mb-1">
                      실시간 상호작용
                    </h4>
                    <p className="text-sm text-gray-300">
                      AI 간 자율적 토론과 협업
                    </p>
                  </div>

                  <div className="text-center">
                    <div className="flex justify-center mb-3">
                      <Sparkles className="w-10 h-10 text-purple-600" />
                    </div>
                    <h4 className="font-semibold text-white mb-1">
                      창의적 시너지
                    </h4>
                    <p className="text-sm text-gray-300">
                      집단 지성을 통한 혁신적 솔루션
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <MainPreview />
        {/* Premium Pricing Plans */}
        <div className="relative z-10 py-12 lg:py-24 bg-black/50 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
                투명하고 합리적인 가격
              </h2>
              <p className="text-lg sm:text-xl text-gray-400">
                모든 플랜에 24시간 AI 비서와 에이전트가 포함됩니다
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {Object.entries(plans).map(([planName, plan], index) => {
                const isRecommended =
                  planName === getRecommendedPlan() &&
                  selectedServices.length > 0;

                return (
                  <div
                    key={planName}
                    className={`relative group transition-all duration-500 ${
                      plan.popular || isRecommended
                        ? "md:scale-110 z-20 mt-8"
                        : "hover:scale-105"
                    }`}
                    onMouseEnter={() => setHoveredPlan(planName)}
                    onMouseLeave={() => setHoveredPlan(null)}
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    {(plan.popular || isRecommended) && (
                      <div className="absolute -top-10 left-1/2 transform -translate-x-1/2">
                        <span
                          className={`bg-gradient-to-r ${plan.gradient} px-4 py-1 rounded-full text-xs font-semibold`}
                        >
                          {isRecommended ? "추천 플랜" : "인기 플랜"}
                        </span>
                      </div>
                    )}

                    <div
                      className={`relative bg-gray-900/80 backdrop-blur-xl rounded-3xl border transition-all duration-300 overflow-hidden ${
                        plan.popular || isRecommended
                          ? "border-transparent"
                          : "border-gray-800 hover:border-gray-700"
                      }`}
                    >
                      {(plan.popular || isRecommended) && (
                        <div
                          className={`absolute inset-0 bg-gradient-to-br ${plan.gradient} opacity-10 `}
                        ></div>
                      )}

                      <div className="relative p-8">
                        <h3 className="text-2xl font-bold mb-2">{planName}</h3>
                        <p className="text-gray-400 mb-6">{plan.desc}</p>

                        <div className="flex items-baseline mb-8">
                          <span className="text-2xl font-bold">
                            {planName === "ZERO1" ? "무료" : `₩${plan.price}`}
                          </span>
                          <span className="text-gray-400 ml-2">/월</span>
                        </div>

                        <ul className="space-y-4 mb-8">
                          {plan.features.map((feature, i) => (
                            <li key={i} className="flex items-start gap-3">
                              <div
                                className={`w-5 h-5 rounded-full bg-gradient-to-r ${plan.gradient} flex items-center justify-center flex-shrink-0 mt-0.5`}
                              >
                                <Check className="w-3 h-3" />
                              </div>
                              <span className="text-gray-300">{feature}</span>
                            </li>
                          ))}
                        </ul>

                        <button
                          className={`w-full py-4 rounded-2xl font-semibold transition-all duration-300 ${
                            plan.popular || isRecommended
                              ? `bg-gradient-to-r ${plan.gradient} hover:shadow-2xl hover:shadow-purple-500/25`
                              : "bg-gray-800 hover:bg-gray-700"
                          }`}
                          onClick={() => {
                            console.log(111);
                            setIsModalOpen(true);
                          }}
                        >
                          시작하기
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="relative z-10 py-12 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              지금 바로 시작하세요
            </h2>
            <p className="text-lg sm:text-xl text-gray-400 mb-8">
              현재 Elva는 일부 환경 제약으로 인해 온라인 가입이 어려워
              <br />
              직접 상담을 통해 계정을 발급해 드리고 있습니다. 불편을 드려
              죄송하며,
              <br />
              번거로우시겠지만 아래 문의하기로 연락 부탁드립니다.
              <br /> (비즈니스 조직을 새롭게 구축하시는 경우)
              <br />
              일반 사용자는 회원가입 후 워크스페이스에 참여하여 바로
              사용가능합니다.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                className="bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-4 rounded-2xl font-semibold text-lg hover:shadow-2xl hover:shadow-purple-500/25 transition-all duration-300"
                onClick={() => setIsModalOpen(true)}
              >
                문의하기
              </button>
            </div>
          </div>
        </div>
      </div>

      <ContactModal
        isOpen={isModalOpen} // 열림 여부 전달
        onClose={() => setIsModalOpen(false)} // 모달 닫기 핸들러
      />
    </>
  );
}
